import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Plus, Loader2, AlertCircle, X } from 'lucide-react';
import { employeeService } from '../services/employeeService';
import { Badge } from '../../../../components/ui/Badge';

const CATEGORIES = [
  { value: 'técnica', label: 'Técnica' },
  { value: 'idioma', label: 'Idioma' },
  { value: 'certificación', label: 'Certificación' },
];

const CATEGORY_STYLES = {
  técnica: 'bg-blue-900/50 text-blue-200',
  idioma: 'bg-violet-900/50 text-violet-200',
  certificación: 'bg-emerald-900/50 text-emerald-200',
};

function CreateSkillModal({ isOpen, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    code: '',
    category: 'técnica',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setForm({ name: '', code: '', category: 'técnica', description: '' });
    setError(null);
  }, [isOpen]);

  const handleNameChange = (name) => {
    setForm((prev) => {
      const next = { ...prev, name };
      if (!prev.code || prev.code === suggestCode(prev.name)) {
        next.code = suggestCode(name);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('El nombre es obligatorio.'); return; }
    if (!form.code.trim()) { setError('El código es obligatorio.'); return; }
    setSaving(true);
    setError(null);
    try {
      await employeeService.createSkill({
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        category: form.category,
        description: form.description.trim(),
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err?.message || 'Error al crear la habilidad.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-800 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-yellow-400" />
            <h3 className="text-lg font-semibold text-white">Nueva Habilidad</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Nombre *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Ej: Soldador TIG"
              required
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Código *</label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="Ej: WELD_TIG"
              required
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Categoría</label>
            <select
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Descripción</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              placeholder="Descripción opcional de la habilidad"
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-800 bg-red-900/20 px-3 py-2 text-sm text-red-300">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 text-sm hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-600 hover:bg-yellow-500 text-white text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {saving ? 'Guardando…' : 'Crear habilidad'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function suggestCode(name) {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

export function SkillsCatalogSection({ createModalOpen, onCreateModalChange, hideList = false }) {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [internalShowModal, setInternalShowModal] = useState(false);

  const showModal = createModalOpen ?? internalShowModal;
  const setShowModal = onCreateModalChange ?? setInternalShowModal;

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeService.getSkillsCatalog();
      setSkills(data);
    } catch (err) {
      setError(err?.message || 'Error al cargar el catálogo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hideList) return;
    loadCatalog();
  }, [loadCatalog, hideList]);

  if (hideList) {
    return (
      <CreateSkillModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onCreated={loadCatalog}
      />
    );
  }

  return (
    <>
      <div className="mt-8 mb-6">
        <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg w-full min-w-0">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-700">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-yellow-400 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold text-white">Catálogo de Habilidades</h2>
                  <p className="mt-1 text-sm text-gray-400">
                    Competencias disponibles para asignar a empleados
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-600 hover:bg-yellow-500 text-white text-sm font-medium transition-colors shrink-0"
              >
                <Plus className="w-4 h-4" />
                Nueva habilidad
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6 w-full min-w-0">
            {loading && (
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Cargando catálogo…
              </div>
            )}

            {error && !loading && (
              <div className="flex items-center gap-2 rounded-lg border border-red-800 bg-red-900/20 px-3 py-2 text-sm text-red-300">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {!loading && !error && skills.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm mb-3">No hay habilidades en el catálogo</p>
                <button
                  type="button"
                  onClick={() => setShowModal(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-yellow-600/50 text-yellow-400 text-sm hover:bg-yellow-600/10 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Crear la primera habilidad
                </button>
              </div>
            )}

            {!loading && !error && skills.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {skills.map((skill) => (
                  <div
                    key={skill.id}
                    className="rounded-xl border border-gray-700 bg-gray-700/40 p-4 flex flex-col gap-2"
                  >
                    <div>
                      <p className="font-medium text-white text-sm leading-tight">{skill.name}</p>
                      <p className="text-xs text-gray-500 font-mono mt-0.5">{skill.code}</p>
                    </div>
                    {skill.category && (
                      <Badge className={CATEGORY_STYLES[skill.category] ?? 'bg-gray-700 text-gray-200'}>
                        {skill.category}
                      </Badge>
                    )}
                    {skill.description && (
                      <p className="text-xs text-gray-400 line-clamp-2">{skill.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateSkillModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onCreated={loadCatalog}
      />
    </>
  );
}
